import { config } from '~/config';

import { useBaseWs } from '~/services/use-base-ws/useBaseWs';
import { CloseReason, WebSocketStatus } from '~/services/use-base-ws/utils';
import {
  NotificationResponseByType,
  NotificationResponseTypes,
  NotificationSocketResponses,
} from './types';

export interface NotificationWsInstance {
  subscribe<T extends NotificationResponseTypes>(
    type: T,
    callback: (
      response: NotificationResponseByType<T>,
      isActiveTab: boolean
    ) => void
  ): () => void;
  unsubscribe: () => void;
  readonly isConnected: boolean;
  readonly status: WebSocketStatus;
  onmessage: ((message: NotificationSocketResponses) => void) | null;
  onVisibilityChange?: (e: Event) => void;
}

export type CreateNotificationWsOptions = {
  onError?: (error: string) => void;
};

export const createNotificationWs = (
  options: CreateNotificationWsOptions = {}
): NotificationWsInstance => {
  const { onError } = options;
  const url = config.api.wsNotifierRoot;

  let isTabActive = true;
  const listeners = new Map<
    NotificationResponseTypes,
    ((v: NotificationSocketResponses, isTabActive: boolean) => void)[]
  >();
  let customOnMessage: ((message: NotificationSocketResponses) => void) | null =
    null;

  const dispatchMessage = (message: NotificationSocketResponses) => {
    const current = listeners.get(message.event.type) ?? [];
    current.forEach(listener => listener(message, isTabActive));
  };

  let visibilityListenerAttached = false;

  const visibilityChange = () => {
    const isAlive =
      base.status !== WebSocketStatus.CLOSED &&
      base.status !== WebSocketStatus.UNINSTANTIATED;
    isTabActive = !document.hidden && isAlive;
    instance.onVisibilityChange?.(new Event('visibilitychange'));
  };

  const base = useBaseWs(url, {
    onOpen: () => {
      if (!visibilityListenerAttached) {
        document.addEventListener('visibilitychange', visibilityChange);
        visibilityListenerAttached = true;
      }
    },
    onClose: (event: CloseEvent) => {
      if (event.reason === CloseReason.NoConnectionRequired) {
        document.removeEventListener('visibilitychange', visibilityChange);
        visibilityListenerAttached = false;
      }
    },
    onMessage: (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as NotificationSocketResponses;
        dispatchMessage(message);
        customOnMessage?.(message);
      } catch (_error) {
        onError?.('WebSocket: Failed to parse message');
      }
    },
    onError: () => onError?.('WebSocket: Transport error'),
    shouldReconnect: () => true,
    reconnectAttempts: 5,
  });

  const instance: NotificationWsInstance = {
    subscribe<T extends NotificationResponseTypes>(
      type: T,
      callback: (
        response: NotificationResponseByType<T>,
        isActiveTab: boolean
      ) => void
    ) {
      const current = listeners.get(type) ?? [];
      listeners.set(type, [
        ...current,
        callback as (
          v: NotificationSocketResponses,
          isTabActive: boolean
        ) => void,
      ]);

      return () => {
        const current = listeners.get(type) ?? [];
        listeners.set(
          type,
          current.filter(c => c !== callback)
        );
      };
    },
    unsubscribe: () => {
      base.disconnect(1000, CloseReason.NoConnectionRequired);
    },
    get isConnected() {
      return base.isConnected;
    },
    get status() {
      return base.status;
    },
    get onmessage() {
      return customOnMessage;
    },
    set onmessage(fn: ((message: NotificationSocketResponses) => void) | null) {
      customOnMessage = fn;
    },
    onVisibilityChange: undefined,
  };

  return instance;
};
